import React, { useMemo, useState } from 'react';
import {
    Box, Typography, Grid, Card, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    Alert, AlertTitle, Button, TextField, 
} from '@mui/material';
import {
    Calendar, User, Filter,ShieldAlert, CheckCircle2,
} from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, LineChart, Line
} from 'recharts';

const PaymentAnalyticsDashboard = ({ ledger = [], onDrillDownTransaction }) => {
    const [selectedAgentFilter, setSelectedAgentFilter] = useState('');
    const [selectedMonthFilter, setSelectedMonthFilter] = useState('');

    // ─── EXTRACT ADVANCED MONTHLY TRACKER MATRIX ───
    const monthlyAnalytics = useMemo(() => {
        const dataMap = {};
        ledger.forEach(tx => {
            if (!tx.recorded_at) return;
            const date = new Date(tx.recorded_at);
            const monthStr = date.toLocaleString('en-IN', { month: 'long', year: 'numeric' });

            if (!dataMap[monthStr]) {
                dataMap[monthStr] = { month: monthStr, totalBooked: 0, cashCollected: 0, receivables: 0, txnCounts: 0 };
            }

            const total = parseFloat(tx.total_fees || 0);
            const paid = parseFloat(tx.amount_paid || 0);

            dataMap[monthStr].totalBooked += total;
            dataMap[monthStr].cashCollected += paid;
            dataMap[monthStr].receivables += (total - paid);
            dataMap[monthStr].txnCounts += 1;
        });
        return Object.values(dataMap);
    }, [ledger]);

    // ─── EXTRACT EMPLOYEE PERFORMANCE METRICS AND RISK PROFILES ───
    const employeeMetrics = useMemo(() => {
        const empMap = {};
        ledger.forEach(tx => {
            const agent = tx.collected_by || tx.recorded_by || "System Automated";
            if (!empMap[agent]) {
                empMap[agent] = {
                    agent, totalBooked: 0, totalCollected: 0, outstandingRisk: 0,
                    accountsCount: 0, fullyPaidCount: 0, criticalAlertsCount: 0
                };
            }

            const total = parseFloat(tx.total_fees || 0);
            const paid = parseFloat(tx.amount_paid || 0);
            const remaining = total - paid;

            empMap[agent].totalBooked += total;
            empMap[agent].totalCollected += paid;
            empMap[agent].outstandingRisk += remaining;
            empMap[agent].accountsCount += 1;

            if (remaining === 0) empMap[agent].fullyPaidCount += 1;
            // Mark as critical risk alert if remaining amount is substantial (e.g., more than 50% of the asset cost)
            if (remaining > 0 && remaining >= (total * 0.5)) {
                empMap[agent].criticalAlertsCount += 1;
            }
        });
        return Object.values(empMap).sort((a, b) => b.totalCollected - a.totalCollected);
    }, [ledger]);

    // ─── GENERATE REAL-TIME ACTIONABLE AGING ALERTS FOR ADMIN/HR ───
    const actionableAlerts = useMemo(() => {
        const alerts = [];
        ledger.forEach(tx => {
            const total = parseFloat(tx.total_fees || 0);
            const paid = parseFloat(tx.amount_paid || 0);
            const delta = total - paid;

            if (delta > 0) {
                alerts.push({
                    id: tx.id,
                    studentName: tx.student_name,
                    courseName: tx.course_name,
                    agent: tx.collected_by || tx.recorded_by || "System Automated",
                    remainingBalance: delta,
                    riskLevel: delta >= (total * 0.5) ? 'CRITICAL' : 'WARNING',
                    recordedAt: tx.recorded_at
                });
            }
        });
        return alerts.sort((a, b) => b.remainingBalance - a.remainingBalance);
    }, [ledger]);

    // ─── DATA PIPELINE FILTER INTERACTION MATRIX ───
    const filteredAlerts = useMemo(() => {
        return actionableAlerts.filter(alt => {
            const matchAgent = selectedAgentFilter ? alt.agent === selectedAgentFilter : true;
            if (!matchAgent) return false;

            if (selectedMonthFilter) {
                const altMonth = new Date(alt.recordedAt).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
                return altMonth === selectedMonthFilter;
            }
            return true;
        });
    }, [actionableAlerts, selectedAgentFilter, selectedMonthFilter]);

    return (
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* AGENT ALERTS & RISK CONTEXT INFRASTRUCTURE STRIP */}
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#081c3b', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, letterSpacing: '0.03em' }}>
                    <ShieldAlert size={16} color="#e52222" /> ENTERPRISE RISK MONITOR & AGENT RECEIVABLES OUTSTANDING ALERTS
                </Typography>

                {/* Advanced Multi-Dimensional Filters */}
                <Box sx={{ bgcolor: '#ffffff', border: '1px solid #d8dde6', borderRadius: '4px', p: 1.5, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#514f4d', textTransform: 'uppercase' }}><Filter size={12} style={{ marginRight: 4 }} /> Filter Workspace Nodes:</Typography>
                    <TextField
                        select
                        size="small"
                        label="Select Performance Agent"
                        value={selectedAgentFilter}
                        onChange={(e) => setSelectedAgentFilter(e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ width: 220, '& label': { fontSize: '13px' } }}
                    >
                        <option value="">All Active BDE Agents</option>
                        {employeeMetrics.map(e => <option key={e.agent} value={e.agent}>{e.agent}</option>)}
                    </TextField>

                    <TextField
                        select
                        size="small"
                        label="Select Timeline Horizon"
                        value={selectedMonthFilter}
                        onChange={(e) => setSelectedMonthFilter(e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ width: 220, '& label': { fontSize: '13px' } }}
                    >
                        <option value="">All Historical Data Horizons</option>
                        {monthlyAnalytics.map(m => <option key={m.month} value={m.month}>{m.month}</option>)}
                    </TextField>

                    {(selectedAgentFilter || selectedMonthFilter) && (
                        <Button variant="text" size="small" onClick={() => { setSelectedAgentFilter(''); setSelectedMonthFilter(''); }} sx={{ textTransform: 'none', color: '#0176d3', fontWeight: 600 }}>
                            Reset Active Filters
                        </Button>
                    )}
                </Box>

                <Grid container spacing={2} sx={{ maxHeight: 240, overflowY: 'auto', pr: 1 }}>
                    {filteredAlerts.length === 0 ? (
                        <Grid item xs={12}>
                            <Alert severity="success" sx={{ borderRadius: '4px', border: '1px solid #bcf0b6', bgcolor: '#eafbe7' }}>
                                <AlertTitle sx={{ fontWeight: 700 }}>Zero Receivable Anomalies Detected</AlertTitle>
                                All records for selected agents/timelines match verified asset collection pipelines perfectly.
                            </Alert>
                        </Grid>
                    ) : (
                        filteredAlerts.slice(0, 6).map((alert, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card variant="outlined" sx={{ borderColor: alert.riskLevel === 'CRITICAL' ? '#fbcbcb' : '#ffeaa3', bgcolor: alert.riskLevel === 'CRITICAL' ? '#fffbfa' : '#fffdf3', borderRadius: '4px' }}>
                                    <Box sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Chip
                                                label={alert.riskLevel}
                                                size="small"
                                                sx={{ fontSize: '9px', fontWeight: 800, borderRadius: '3px', height: 18, bgcolor: alert.riskLevel === 'CRITICAL' ? '#cb2a2a' : '#b27b00', color: '#fff' }}
                                            />
                                            <Typography variant="caption" sx={{ color: '#706e6b', fontWeight: 600 }}>
                                                {new Date(alert.recordedAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#081c3b' }}>{alert.studentName}</Typography>
                                        <Typography variant="caption" sx={{ color: '#514f4d', display: 'block' }}>Track: {alert.courseName}</Typography>
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #dddbda', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#706e6b', display: 'block', fontSize: '10px' }}>RESPONSIBLE AGENT</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0176d3', fontSize: '12px' }}>{alert.agent}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" sx={{ color: '#706e6b', display: 'block', fontSize: '10px' }}>DUE DELTA</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '13px' }}>₹{alert.remainingBalance.toLocaleString('en-IN')}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            </Box>

            {/* MONTHLY AND EMPLOYEE ANALYTICAL DATA ARRAYS */}
            <Grid container spacing={3}>

                {/* ADVANCED MONTHLY BALANCE TRACKER */}
                <Grid item xs={12} lg={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '4px', borderColor: '#d8dde6', bgcolor: '#fff' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#081c3b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Calendar size={16} color="#0176d3" /> Monthly Financial Performance Horizon Ledger
                        </Typography>
                        <Box sx={{ width: '100%', height: 180, mb: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={monthlyAnalytics} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f5f9" />
                                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#514f4d' }} />
                                    <YAxis tick={{ fontSize: 10, fill: '#514f4d' }} />
                                    <Tooltip contentStyle={{ fontSize: '11px', background: '#081c3b', color: '#fff' }} />
                                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                                    <Line type="monotone" name="Booked Volume" dataKey="totalBooked" stroke="#0176d3" strokeWidth={2} dot={{ r: 4 }} />
                                    <Line type="monotone" name="Liquids Secured" dataKey="cashCollected" stroke="#2e7d32" strokeWidth={2} dot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                        <TableContainer sx={{ maxHeight: 220 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontSize: '10px', color: '#514f4d', fontWeight: 700, bgcolor: '#f3f5f9', py: 1 } }}>
                                        <TableCell>Accounting Horizon</TableCell>
                                        <TableCell align="right">Booked Pipeline</TableCell>
                                        <TableCell align="right">Cash Inflow</TableCell>
                                        <TableCell align="right">Outstanding Balance</TableCell>
                                        <TableCell align="center">Volume</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {monthlyAnalytics.map((row, i) => (
                                        <TableRow key={i} hover sx={{ '& td': { fontSize: '12px', py: 0.8 } }}>
                                            <TableCell sx={{ fontWeight: 700 }}>{row.month}</TableCell>
                                            <TableCell align="right">₹{row.totalBooked.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 600 }}>₹{row.cashCollected.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="right" sx={{ color: row.receivables > 0 ? '#d32f2f' : 'inherit', fontWeight: 600 }}>₹{row.receivables.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="center"><Chip label={`${row.txnCounts} tx`} size="small" sx={{ height: 16, fontSize: '10px', borderRadius: '3px' }} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* ADVANCED EMPLOYEE WISE PAYMENT TRACKER & BALANCES */}
                <Grid item xs={12} lg={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '4px', borderColor: '#d8dde6', bgcolor: '#fff' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#081c3b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <User size={16} color="#0176d3" /> Employee-Wise Revenue & Capital Outstanding Audit Matrix
                        </Typography>
                        <Box sx={{ width: '100%', height: 180, mb: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={employeeMetrics} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f5f9" />
                                    <XAxis dataKey="agent" tick={{ fontSize: 10, fill: '#514f4d' }} />
                                    <YAxis tick={{ fontSize: 10, fill: '#514f4d' }} />
                                    <Tooltip contentStyle={{ fontSize: '11px', background: '#081c3b', color: '#fff' }} />
                                    <Legend wrapperStyle={{ fontSize: '10px' }} />
                                    <Bar name="Cash Secured" dataKey="totalCollected" stackId="a" fill="#2e7d32" />
                                    <Bar name="Outstanding Balance Due" dataKey="outstandingRisk" stackId="a" fill="#e52222" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                        <TableContainer sx={{ maxHeight: 220 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontSize: '10px', color: '#514f4d', fontWeight: 700, bgcolor: '#f3f5f9', py: 1 } }}>
                                        <TableCell>Performance Agent (BDE)</TableCell>
                                        <TableCell align="right">Booked Pipeline</TableCell>
                                        <TableCell align="right">Liquids Secured</TableCell>
                                        <TableCell align="right">Total Balance Due</TableCell>
                                        <TableCell align="center">Critical Flags</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {employeeMetrics.map((row, i) => (
                                        <TableRow key={i} hover sx={{ '& td': { fontSize: '12px', py: 0.8 } }}>
                                            <TableCell sx={{ fontWeight: 700, color: '#0176d3' }}>{row.agent}</TableCell>
                                            <TableCell align="right">₹{row.totalBooked.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 600 }}>₹{row.totalCollected.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="right" sx={{ color: row.outstandingRisk > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 700 }}>₹{row.outstandingRisk.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="center">
                                                {row.criticalAlertsCount > 0 ? (
                                                    <Chip label={`${row.criticalAlertsCount} Alert`} size="small" sx={{ height: 16, fontSize: '10px', borderRadius: '3px', bgcolor: '#fdf2f2', color: '#cb2a2a', fontWeight: 700, border: '1px solid #fbcbcb' }} />
                                                ) : (
                                                    <CheckCircle2 size={14} color="#2e7d32" style={{ margin: 'auto' }} />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    );
};

export default PaymentAnalyticsDashboard;